import React, { useState } from 'react';
import Icon from '../../components/AppIcon';

const ACCENT = '#F97316'; // Orange accent for Comercial

const PendingBadge = () => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
    style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.20)' }}>
    <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
    <span className="font-oranienbaum text-orange-400 text-xs tracking-widest uppercase">Módulo en construcción · Sin datos reales todavía</span>
  </div>
);

const KPICard = ({ label, value, icon, color, sub, badge }) => (
  <div className="rounded-xl p-4 flex flex-col gap-2"
    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
    <div className="flex items-center justify-between">
      <span className="font-oranienbaum text-white/50 text-xs tracking-wide uppercase">{label}</span>
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
        <Icon name={icon} size={14} style={{ color }} />
      </div>
    </div>
    <div className="flex items-end gap-2">
      <span className="font-oranienbaum font-bold text-white text-2xl">{value}</span>
      {badge && (
        <span className="font-oranienbaum text-xs px-2 py-0.5 rounded-full mb-0.5"
          style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
          {badge}
        </span>
      )}
    </div>
    {sub && <span className="font-oranienbaum text-white/30 text-xs">{sub}</span>}
  </div>
);

const SectionTitle = ({ icon, label, color = ACCENT }) => (
  <div className="flex items-center gap-2 mb-3">
    <div className="w-1 h-5 rounded-full" style={{ background: color }} />
    <Icon name={icon} size={15} style={{ color }} />
    <span className="font-oranienbaum font-semibold text-white/80 text-sm tracking-wide uppercase">{label}</span>
  </div>
);

const kpisClientes = [
  { label: 'Clientes Totales', value: '—', icon: 'Users', color: ACCENT, sub: 'Sin datos' },
  { label: 'Clientes Tienda', value: '—', icon: 'ShoppingBag', color: '#34D399', sub: 'Sin datos' },
  { label: 'Clientes Distribución', value: '—', icon: 'Truck', color: '#60A5FA', sub: 'Sin datos' },
  { label: 'Clientes Sala Loncheado', value: '—', icon: 'Scissors', color: '#D6EF4E', sub: 'Sin datos' },
];

const kpisComunicaciones = [
  { label: 'Conversaciones Abiertas', value: '—', icon: 'MessageSquare', color: ACCENT, sub: 'Sin datos' },
  { label: 'Resueltas Automáticamente', value: '—', icon: 'Bot', color: '#34D399', sub: 'Sin datos' },
  { label: 'Requieren Atención', value: '—', icon: 'AlertCircle', color: '#FBBF24', sub: 'Sin datos' },
  { label: 'Urgentes', value: '—', icon: 'Zap', color: '#F87171', sub: 'Sin datos' },
];

const kpisPedidos = [
  { label: 'Pedidos por Comunicaciones', value: '—', icon: 'ShoppingCart', color: ACCENT, sub: 'Sin datos' },
  { label: 'Conversión Conv. → Pedido', value: '—', icon: 'TrendingUp', color: '#A78BFA', sub: 'Sin datos' },
  { label: 'Campañas Activas', value: '—', icon: 'Megaphone', color: '#F472B6', sub: 'Sin datos' },
  { label: 'Segmentos Definidos', value: '—', icon: 'Filter', color: '#38BDF8', sub: 'Sin datos' },
];

const canalesPrevistos = [
  { canal: 'WhatsApp', icon: 'MessageCircle', color: '#25D366', estado: 'Pendiente de conexión' },
  { canal: 'Instagram Direct', icon: 'Instagram', color: '#E1306C', estado: 'Pendiente de conexión' },
  { canal: 'Facebook Messenger', icon: 'Facebook', color: '#1877F2', estado: 'Pendiente de conexión' },
  { canal: 'Email', icon: 'Mail', color: '#60A5FA', estado: 'Pendiente de conexión' },
  { canal: 'Web / Chat', icon: 'Globe', color: '#A78BFA', estado: 'Futuro' },
];

const estadosConversacion = [
  { estado: 'AUTOMÁTICO', color: '#34D399', desc: 'IA gestionando la conversación' },
  { estado: 'REQUIERE ATENCIÓN', color: '#FBBF24', desc: 'Necesita revisión humana' },
  { estado: 'URGENTE', color: '#F87171', desc: 'Escalado urgente' },
  { estado: 'MODO HUMANO', color: '#60A5FA', desc: 'Operador tomó el control' },
  { estado: 'CERRADO', color: '#6B7280', desc: 'Conversación finalizada' },
];

const ComercialDashboard = () => {
  const [dateFilter, setDateFilter] = useState('Mes');
  const dateFilters = ['Hoy', 'Semana', 'Mes', 'Trimestre'];

  return (
    <div className="min-h-screen px-4 py-6 md:px-8"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <nav className="flex items-center gap-2 text-xs font-oranienbaum text-white/30 mb-2">
            <span>🤝 Comercial</span>
            <Icon name="ChevronRight" size={12} />
            <span className="text-white/60">Dashboard</span>
          </nav>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-2xl">🤝</span>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Dashboard Comercial</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-10">CRM Transversal · Las Dos Doncellas</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <PendingBadge />
          <div className="flex gap-1.5 flex-wrap justify-end">
            {dateFilters?.map(f => (
              <button key={f} onClick={() => setDateFilter(f)}
                className={`px-3 py-1 rounded-lg text-xs font-oranienbaum transition-all ${dateFilter === f
                  ? 'text-[#0A0A0A] font-semibold' : 'text-white/50 hover:text-white/80'}`}
                style={dateFilter === f ? { background: ACCENT } : { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPIs Clientes */}
      <div className="mb-6">
        <SectionTitle icon="Users" label="Clientes" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpisClientes?.map(k => <KPICard key={k?.label} {...k} />)}
        </div>
      </div>

      {/* KPIs Comunicaciones */}
      <div className="mb-6">
        <SectionTitle icon="MessageSquare" label="Comunicaciones Omnicanal" color="#FBBF24" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpisComunicaciones?.map(k => <KPICard key={k?.label} {...k} />)}
        </div>
      </div>

      {/* KPIs Pedidos / Campañas */}
      <div className="mb-6">
        <SectionTitle icon="TrendingUp" label="Pedidos y Campañas" color="#A78BFA" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpisPedidos?.map(k => <KPICard key={k?.label} {...k} />)}
        </div>
      </div>

      {/* Canales previstos + Estados */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">

        {/* Canales */}
        <div className="rounded-xl p-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <SectionTitle icon="Radio" label="Canales Omnicanal Previstos" color={ACCENT} />
          <div className="space-y-2">
            {canalesPrevistos?.map(c => (
              <div key={c?.canal} className="flex items-center justify-between py-2 px-3 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ background: `${c?.color}18`, border: `1px solid ${c?.color}30` }}>
                    <Icon name={c?.icon} size={14} style={{ color: c?.color }} />
                  </div>
                  <span className="font-oranienbaum text-white/80 text-sm">{c?.canal}</span>
                </div>
                <span className="font-oranienbaum text-xs px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  {c?.estado}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Estados de conversación */}
        <div className="rounded-xl p-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <SectionTitle icon="Workflow" label="Estados de Conversación" color="#60A5FA" />
          <div className="space-y-2">
            {estadosConversacion?.map(e => (
              <div key={e?.estado} className="flex items-center justify-between py-2 px-3 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: e?.color }} />
                  <span className="font-oranienbaum text-white/80 text-sm font-semibold tracking-wide">{e?.estado}</span>
                </div>
                <span className="font-oranienbaum text-white/35 text-xs text-right">{e?.desc}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 rounded-lg"
            style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)' }}>
            <p className="font-oranienbaum text-orange-400/80 text-xs leading-relaxed">
              <strong>Recepcionista Digital:</strong> La IA clasificará mensajes por intención (PEDIDO, CONSULTA, HORARIO, INCIDENCIA…) y departamento (TIENDA, DISTRIBUCIÓN, SALA). Cuando no pueda resolver, escalará a modo HUMANO.
            </p>
          </div>
        </div>
      </div>

      {/* Arquitectura prevista */}
      <div className="rounded-xl p-5"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <SectionTitle icon="Database" label="Arquitectura de Datos Prevista" color="#D6EF4E" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {['clientes (maestro)', 'cliente_areas', 'cliente_canales', 'conversaciones', 'mensajes', 'campañas']?.map(t => (
            <div key={t} className="rounded-lg px-3 py-2 text-center"
              style={{ background: 'rgba(214,239,78,0.05)', border: '1px solid rgba(214,239,78,0.12)' }}>
              <Icon name="Table2" size={13} className="mx-auto mb-1 text-[#D6EF4E]/50" />
              <span className="font-oranienbaum text-[#D6EF4E]/70 text-xs">{t}</span>
            </div>
          ))}
        </div>
        <p className="font-oranienbaum text-white/25 text-xs mt-3">
          Tablas pendientes de creación tras aprobación del esquema. Ver informe de arquitectura.
        </p>
      </div>
    </div>
  );
};

export default ComercialDashboard;
